/** Student profile detail tab. */
import * as React from 'react';
import { GraduationCap, FileCheck, Eye, Download } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { useDepartments } from '@/hooks/useDepartments';
import { InternalCommentBox } from '@/components/common/InternalCommentBox';
import {
  SCHOOL_SITUATION_CONFIG,
  STEP_COMPLETION_CONFIG,
} from '@/components/students/student-configs';
import type { StudentRecord } from '@/types/remote/student.types';
import {
  downloadViaBlob,
  formatDate,
  getCompletionStatus,
  getStudentGenderLabel,
  openDocumentPreview,
} from '@/components/students/remote/student-table-utils';

/** Props for {@link StudentProfileTab}. */
interface StudentProfileTabProps {
  detailRecord: StudentRecord;
  sections: Record<string, unknown>;
}

/** Student profile detail tab. */
export const StudentProfileTab: React.FC<StudentProfileTabProps> = ({ detailRecord, sections }) => {
  const { data: departments = [] } = useDepartments();

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={GraduationCap} title="Inscription" colorName="color-2" colSpan={1}>
        {(() => {
          const ri = sections['registration_information'] as any;
          const pct = ri?.sectionCompletion?.completionPercentage ?? 0;
          const completionStatus = getCompletionStatus(pct);
          const completionConfig = STEP_COMPLETION_CONFIG[completionStatus];
          return (
            <>
              <div className="flex items-center justify-between mb-3">
                <LabelBadge
                  label={`${pct}%`}
                  icon={completionConfig.icon}
                  variant={completionConfig.badgeVariant}
                  color={completionConfig.badgeColor}
                  size="sm"
                />
              </div>
              {ri ? (
                <div className="space-y-3">
                  <DetailField
                    label="Nom / Prénom"
                    value={`${ri.firstName ?? detailRecord.user?.firstName ?? '—'} ${ri.lastName ?? detailRecord.user?.lastName ?? '—'}`}
                  />
                  <DetailField
                    label="Date de naissance"
                    value={formatDate(ri.dateOfBirth ?? detailRecord.user?.birthDate)}
                  />
                  <DetailField label="Genre" value={getStudentGenderLabel(ri.gender)} />
                  <DetailField
                    label="Situation scolaire"
                    value={
                      ri.schoolSituation ? (
                        <LabelBadge
                          label={
                            SCHOOL_SITUATION_CONFIG[ri.schoolSituation]?.label ?? ri.schoolSituation
                          }
                          icon={SCHOOL_SITUATION_CONFIG[ri.schoolSituation]?.icon ?? GraduationCap}
                          variant={
                            SCHOOL_SITUATION_CONFIG[ri.schoolSituation]?.badgeVariant ?? 'outlined'
                          }
                          color={SCHOOL_SITUATION_CONFIG[ri.schoolSituation]?.badgeColor}
                          size="sm"
                        />
                      ) : (
                        '—'
                      )
                    }
                  />
                  <DetailField label="Niveau scolaire" value={ri.level?.levelName ?? ri.level} />
                  {ri.establishmentDepartment && (
                    <DetailField
                      label="Département de l'établissement"
                      value={(() => {
                        const code =
                          typeof ri.establishmentDepartment === 'string'
                            ? ri.establishmentDepartment
                            : ((ri.establishmentDepartment as any)?.departmentId ?? '');
                        const dept = departments.find((d) => d.code === code);
                        return dept ? dept.name : code;
                      })()}
                    />
                  )}
                  <DetailField
                    label="Établissement"
                    value={
                      Array.isArray(ri.schoolEstablishment) && ri.schoolEstablishment.length > 0
                        ? (ri.schoolEstablishment[0]?.establishmentName ??
                          ri.schoolEstablishment[0])
                        : ri.schoolEstablishment
                    }
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Informations d'inscription non renseignées.
                </p>
              )}
            </>
          );
        })()}
      </DetailsCard>

      <DetailsCard icon={FileCheck} title="Documents scolaires" colorName="color-1" colSpan={1}>
        {(() => {
          const docs = sections['documents'] as any;
          const schoolReport = docs?.schoolReport;
          if (!schoolReport?.documentLink) {
            return <p className="text-sm text-muted-foreground">Aucun bulletin scolaire soumis.</p>;
          }
          return (
            <div className="space-y-3">
              <DetailField
                label="Bulletin scolaire"
                value={schoolReport.documentName || 'Document téléchargé'}
              />
              {schoolReport.documentLink && (
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-muted"
                    onClick={() => void openDocumentPreview(schoolReport.documentLink)}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Aperçu
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-muted"
                    onClick={() =>
                      downloadViaBlob(
                        schoolReport.documentLink,
                        schoolReport.documentName ?? 'bulletin.pdf',
                      )
                    }
                  >
                    <Download className="w-3.5 h-3.5" />
                    Télécharger
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </DetailsCard>
      <InternalCommentBox
        userId={detailRecord.userId}
        comment={detailRecord.user?.internalComment ?? null}
      />
    </div>
  );
};
